import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PartnerLogos } from '../components/PartnerLogos';

const BRAND_LOGO_LIGHT = "/benchmark-ph-light.png";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex items-center gap-4">
          <img src={BRAND_LOGO_LIGHT} alt="Benchmark PH" className="h-10 object-contain" />
          <div>
            <h1 className="font-semibold text-primary text-lg">Privacy Policy</h1>
            <p className="text-xs text-slate-500">Benchmark PH — IT-BPM ESG Pulse Navigator</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <Link to="/auth" className="inline-flex items-center gap-2 text-slate-500 hover:text-primary mb-8 transition-colors text-sm" data-testid="back-to-auth">
          <ArrowLeft className="w-4 h-4" />
          Back to Sign Up
        </Link>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-10 shadow-sm">
          <h1 className="text-2xl md:text-3xl font-bold text-primary mb-1">Privacy Policy</h1>
          <p className="text-slate-500 text-sm mb-8">for IBPAP ESG & Double Materiality Questionnaire</p>

          <div className="prose prose-slate prose-sm max-w-none space-y-6" data-testid="privacy-content">
            <section>
              <h2 className="text-lg font-semibold text-primary mt-0">1. Purpose of Data Collection</h2>
              <p>The IBPAP ESG and Double Materiality Questionnaire ("Questionnaire") is conducted to support the IT-BPM industry's sustainability initiatives, including:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Identification and prioritization of material ESG issues</li>
                <li>Development of industry-level sustainability strategies and disclosures</li>
                <li>Alignment with global frameworks such as EFRAG (CSRD), GRI, SASB, and TCFD</li>
                <li>Strengthening compliance with data privacy, environmental, and social governance standards</li>
              </ul>
              <p>As highlighted in the IBPAP Double Materiality Assessment, data privacy and security compliance is a high-priority issue for the industry, given the sensitivity of information handled.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">2. Data Controller and Processor</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Data Controller:</strong> IT & Business Process Association of the Philippines (IBPAP)</li>
                <li><strong>Data Processor:</strong> SusTek Pte Ltd ("SusTek"), appointed to design, administer, and manage the Questionnaire and related ESG analytics</li>
              </ul>
              <p>Both parties are committed to ensuring that all personal and organizational data is handled responsibly, securely, and in compliance with applicable laws.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">3. Types of Data Collected</h2>
              <p>The Questionnaire may collect the following categories of data:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Organizational Data:</strong> ESG policies, operational metrics, sustainability practices</li>
                <li><strong>Professional Information:</strong> Role, department, and contact details of respondents</li>
                <li><strong>Perception and Opinion Data:</strong> Responses related to ESG risks, impacts, and priorities</li>
                <li><strong>Limited Personal Data:</strong> Only where necessary for validation, follow-up, or stakeholder engagement</li>
              </ul>
              <p>Sensitive personal data is not intentionally collected, unless explicitly required and consented to.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">4. Legal Basis for Processing</h2>
              <p>Data is processed based on:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Legitimate Interest:</strong> To advance IBPAP's ESG program and industry sustainability objectives</li>
                <li><strong>Consent:</strong> Provided by respondents upon participation in the Questionnaire</li>
                <li><strong>Regulatory Compliance:</strong> Alignment with applicable ESG reporting and data protection frameworks</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">5. Use of Data</h2>
              <p>Collected data will be used for:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>ESG analysis, benchmarking, and reporting at an aggregated and anonymized level</li>
                <li>Development of the IBPAP Double Materiality Matrix and sustainability disclosures</li>
                <li>Industry insights, policy recommendations, and stakeholder engagement</li>
              </ul>
              <p>No individual organization or respondent will be identified in public reports without explicit consent.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">6. Data Sharing and Disclosure</h2>
              <p>Data may be shared under the following conditions:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>With IBPAP ESG Committee and authorized stakeholders for validation and program development</li>
                <li>With regulators or auditors, where required by law or compliance obligations</li>
                <li>With trusted service providers, strictly under confidentiality and data protection agreements</li>
              </ul>
              <p>No data will be sold or used for unrelated commercial purposes.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">7. Data Security</h2>
              <p>SusTek and IBPAP implement appropriate technical and organizational safeguards, including:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Secure cloud storage with access controls</li>
                <li>Encryption of sensitive data in transit and at rest</li>
                <li>Role-based access management (RBAC)</li>
                <li>Regular security reviews and compliance checks</li>
              </ul>
              <p>These measures align with best practices expected in the IT-BPM sector, where data protection is critical to maintaining trust and avoiding regulatory risks.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">8. Data Retention</h2>
              <p>Data will be retained only for as long as necessary to fulfill the purposes outlined above, including ESG reporting cycles, regulatory and audit requirements, and program evaluation and continuous improvement.</p>
              <p>After this period, data will be securely deleted or anonymized.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">9. Cross-Border Data Transfers</h2>
              <p>Given the regional and global nature of the IT-BPM industry:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Data may be processed or stored outside the Philippines</li>
                <li>Appropriate safeguards (e.g., contractual clauses, secure infrastructure) will be implemented</li>
                <li>Transfers will comply with applicable laws such as Philippines Data Privacy Act (DPA), GDPR, and Singapore PDPA</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">10. Data Subject Rights</h2>
              <p>Participants have the right to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Access and review their data</li>
                <li>Request correction of inaccurate information</li>
                <li>Withdraw consent (where applicable)</li>
                <li>Request deletion or restriction of processing, subject to legal limitations</li>
              </ul>
              <p>Requests may be directed to IBPAP or SusTek through the contact details provided below.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">11. Consent and Participation</h2>
              <p>By participating in this Questionnaire, respondents acknowledge and agree that:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Their data will be processed in accordance with this Privacy Policy</li>
                <li>Their responses may be used in aggregated ESG analysis and reporting</li>
                <li>Participation is voluntary, and they may choose not to provide certain information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">12. Contact Information</h2>
              <p>For questions, requests, or concerns regarding this Privacy Policy or data handling practices, please contact:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>IBPAP ESG Program Team</strong> — <a href="mailto:dpo@ibpap.org" className="text-accent hover:underline">dpo@ibpap.org</a></li>
                <li><strong>SusTek Pte Ltd</strong> — <a href="mailto:dpo@sustek.ai" className="text-accent hover:underline">dpo@sustek.ai</a></li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">13. Updates to this Policy</h2>
              <p>This Privacy Policy may be updated periodically to reflect changes in regulations, industry standards, or program requirements. The latest version will always be made available within the Questionnaire platform.</p>
            </section>
          </div>
        </div>
      </main>

      <footer className="px-4 py-6 text-center text-slate-500 text-sm border-t border-slate-200 bg-white">
        <PartnerLogos className="mb-3" />
        Developed for <span className="text-slate-600">IBPAP</span> by{' '}
        <a href="https://sustek.ai/solutions/soctech" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 hover:underline">
          SusTek SocTech Labs
        </a>
      </footer>
    </div>
  );
}
